import {
  ColumnDef,
  PaginationState,
  Row,
  RowModel,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import { TextCell } from 'components/table/cells/TextCell'
import { TableComponent } from 'components/table/TableComponent'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { TruncationTooltip } from 'features/timeSeries/components/TruncationTooltip'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { useSensorDataAvailability } from 'features/timeSeries/hooks/useSensorDataAvailability'
import { AlertTriangle } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { BaseSensorEntry, InputType } from 'types'

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  thead,
  thead tr,
  thead th {
    background: ${CHART_COLORS.BACKGROUND} !important;
  }

  thead {
    box-shadow: 0 1px 0 ${({ theme }) => theme.forecast.cardDividerBackground};
  }

  tbody tr,
  tbody td {
    background: transparent !important;
  }

  td {
    position: relative;
    z-index: 0;
  }
`

const TagNameCellContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 0.25rem;
  min-width: 0;
  width: 100%;
`

const TagNameText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TagNameCellStack = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
`

const TagDetailLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.5rem;
  margin-top: 0.125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.6875rem;
  line-height: 1rem;
  min-width: 0;
`

const TagDetailItem = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`

const TagDetailLabel = styled.span`
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const NoDataIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.warning};
  flex-shrink: 0;
  height: 0.875rem;
  width: 0.875rem;
`

const idsMatch = (first: string[], second: string[]): boolean =>
  first.length === second.length &&
  first.every((id, index) => id === second[index])

const getMatchingOrderedIds = (
  orderedIds: string[],
  fallbackIds: string[]
): string[] => {
  if (orderedIds.length !== fallbackIds.length) {
    return fallbackIds
  }

  const fallbackIdSet = new Set(fallbackIds)
  return orderedIds.every((id) => fallbackIdSet.has(id))
    ? orderedIds
    : fallbackIds
}

const TagNameCell: React.FC<{
  name: string
  hasNoRecentData: boolean
  showDetails?: boolean
  instrumentTag?: string
  pointSource?: string
}> = ({ name, hasNoRecentData, showDetails, instrumentTag, pointSource }) => {
  const hasDetails = Boolean(instrumentTag || pointSource)
  return (
    <TagNameCellStack>
      <TagNameCellContainer>
        <TruncationTooltip
          side='left'
          label={name}
          style={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
          <TagNameText>{name}</TagNameText>
        </TruncationTooltip>
        {hasNoRecentData && (
          <ChartTooltip
            side='left'
            label='No data in the last 24 hours'
            style={{ display: 'inline-flex', justifySelf: 'end' }}>
            <NoDataIndicator aria-label='No data in the last 24 hours'>
              <AlertTriangle size={12} strokeWidth={2.5} />
            </NoDataIndicator>
          </ChartTooltip>
        )}
      </TagNameCellContainer>
      {showDetails && hasDetails && (
        <TagDetailLine>
          {instrumentTag && (
            <TagDetailItem>
              <TagDetailLabel>Instrument </TagDetailLabel>
              {instrumentTag}
            </TagDetailItem>
          )}
          {pointSource && (
            <TagDetailItem>
              <TagDetailLabel>Source </TagDetailLabel>
              {pointSource}
            </TagDetailItem>
          )}
        </TagDetailLine>
      )}
    </TagNameCellStack>
  )
}

// Match the Tag Name column's tooltip surface; the shared TextCell truncation
// tooltip uses the black TooltipWrapper, which looks different from ChartTooltip.
// The tooltip only appears when the description is actually cut off.
const DescriptionCell: React.FC<{ description?: string }> = ({
  description,
}) =>
  description ? (
    <TruncationTooltip
      side='left'
      label={description}
      style={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
      <TagNameText>{description}</TagNameText>
    </TruncationTooltip>
  ) : (
    <TextCell text='' truncate />
  )

interface AllTagsTableProps {
  filteredTags: BaseSensorEntry[]
  hasRecentDataBySensorId?: Record<string, boolean | undefined>
  enableAvailabilitySurvey?: boolean
  selectedTags: BaseSensorEntry[]
  onToggleTag: (tag: BaseSensorEntry) => void
  showDetails?: boolean
}

export const AllTagsTable: React.FC<AllTagsTableProps> = ({
  filteredTags,
  hasRecentDataBySensorId: providedHasRecentDataBySensorId,
  enableAvailabilitySurvey = true,
  selectedTags,
  onToggleTag,
  showDetails = false,
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [orderedTagIds, setOrderedTagIds] = useState<string[]>([])
  const filteredTagIds = useMemo(
    () => filteredTags.map((tag) => tag.id),
    [filteredTags]
  )
  const visibleTagIds = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize
    return getMatchingOrderedIds(orderedTagIds, filteredTagIds).slice(
      start,
      start + pagination.pageSize
    )
  }, [filteredTagIds, orderedTagIds, pagination.pageIndex, pagination.pageSize])
  const { hasRecentDataBySensorId: visibleHasRecentDataBySensorId } =
    useSensorDataAvailability(
      providedHasRecentDataBySensorId || !enableAvailabilitySurvey
        ? []
        : visibleTagIds
    )
  const hasRecentDataBySensorId =
    providedHasRecentDataBySensorId ?? visibleHasRecentDataBySensorId

  const handleFilteredDataChange = useCallback(
    (rowModel: RowModel<BaseSensorEntry>) => {
      const nextIds = rowModel.rows.map((row) => row.original.id)
      setOrderedTagIds((prevIds) =>
        idsMatch(prevIds, nextIds) ? prevIds : nextIds
      )
    },
    []
  )

  const rowSelection = useMemo(() => {
    const selection: RowSelectionState = {}
    selectedTags.forEach((selectedTag) => {
      const index = filteredTags.findIndex((tag) => tag.id === selectedTag.id)
      if (index !== -1) {
        selection[selectedTag.id] = true
      }
    })
    return selection
  }, [selectedTags, filteredTags])

  const handleRowSelectionChange = (
    updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
  ) => {
    const newSelection =
      typeof updater === 'function' ? updater(rowSelection) : updater

    const previousSelectedIds = new Set(Object.keys(rowSelection))
    const newSelectedIds = new Set(Object.keys(newSelection))

    filteredTags.forEach((tag) => {
      const wasSelected = previousSelectedIds.has(tag.id)
      const isSelected = newSelectedIds.has(tag.id)

      if (wasSelected !== isSelected) {
        onToggleTag(tag)
      }
    })
  }

  const handleRowClick = (
    row: Row<BaseSensorEntry>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (
      event.target instanceof HTMLInputElement &&
      (event.target.type === 'checkbox' ||
        event.target.closest('input[type="checkbox"]'))
    ) {
      return
    }

    const toggleHandler = row.getToggleSelectedHandler()
    toggleHandler(event)
  }

  const columns = useMemo<ColumnDef<BaseSensorEntry>[]>(
    () => [
      {
        accessorKey: 'data.name',
        id: 'name',
        header: 'Tag Name',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TagNameCell
            name={row.original.data.name ?? ''}
            hasNoRecentData={hasRecentDataBySensorId[row.original.id] === false}
            showDetails={showDetails}
            instrumentTag={row.original.data.instrument_tag}
            pointSource={row.original.data.point_source}
          />
        ),
      },
      {
        accessorKey: 'data.description',
        id: 'description',
        header: 'Description',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <DescriptionCell description={row.original.data.description} />
        ),
      },
    ],
    [hasRecentDataBySensorId, showDetails]
  )

  return (
    <TableContainer>
      <TableComponent
        data={filteredTags}
        isLoadingData={false}
        columns={columns}
        itemDescriptiveName='Tags'
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        showFilterBar={false}
        showFooter={true}
        enableRowSelection={true}
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        onFilteredDataChange={handleFilteredDataChange}
        getRowId={(row) => row.id}
        onRowClick={handleRowClick}
        cellPadding='0.125rem 0.25rem'
        cellFontSize='0.75rem'
        borderSpacing='0'
      />
    </TableContainer>
  )
}
